import { useCallback, useRef, useState } from 'react';
import { appointmentsApi } from '../../../services/bookingApi';
import { parseSlotAsWallClock, wallClockToISO } from '../../BookingConfiguration/utils';
import { GeneratedSlot } from '../utils/generatedAvailability';

/**
 * Fetches the bookable slots for a time window from the backend
 * (GET /appointments/slots/) and exposes them as wall-clock Date pairs so
 * they plot at the business's local time regardless of the browser timezone —
 * consistent with how appointment events are parsed.
 *
 * The API already excludes booked / past / non-working times and may return
 * overnight slots that span two days.
 */
export const useAvailableSlots = (smbId: string, timezone?: string) => {
  const [availableSlots, setAvailableSlots] = useState<GeneratedSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  // Cancel the previous in-flight request before starting a new one.
  const abortRef = useRef<AbortController | null>(null);

  const fetchAvailableSlots = useCallback(async (start: Date, end: Date) => {
    if (!smbId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoadingSlots(true);
    try {
      const raw = await appointmentsApi.getAvailableSlots(
        {
          smb_id: smbId,
          start_time: wallClockToISO(start, timezone),
          end_time: wallClockToISO(end, timezone),
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      const parsed: GeneratedSlot[] = raw
        .map((slot) => {
          const slotStart = parseSlotAsWallClock(slot.start_time) ?? new Date(slot.start_time);
          const slotEnd = parseSlotAsWallClock(slot.end_time) ?? new Date(slot.end_time);
          return { start: slotStart, end: slotEnd };
        })
        .filter((slot) => !isNaN(slot.start.getTime()) && !isNaN(slot.end.getTime()));

      setAvailableSlots(parsed);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('[useAvailableSlots] fetchAvailableSlots failed', err);
      setAvailableSlots([]);
    } finally {
      if (!controller.signal.aborted) setIsLoadingSlots(false);
    }
  }, [smbId, timezone]);

  return { availableSlots, isLoadingSlots, fetchAvailableSlots };
};
